import {
    EXTERNAL_NAME_STORAGE_KEY,
    MEET_BOOTSTRAP_NAME_STORAGE_KEY,
    IN_ROOM,
    MEET_PAGE_RELOAD_SESSION_KEY,
    TOKEN_STORAGE_KEY,
    VCS_CALL_ID_STORAGE_KEY,
    VCS_CALL_INITIATOR_ID_STORAGE_KEY,
    VCS_CALL_POLICY_SESSION_KEY,
    VCS_DISMISSED_WAITING_IDS_KEY,
    VCS_DISMISSED_WAITING_SET_KEY,
    VCS_MEET_MESSENGER_BOOTSTRAP_ROOM_KEY,
    VCS_MODERATOR_BYPASS_PREFIX,
    VCS_PAGE_RELOADING_KEY,
    VCS_WAITING_CALL_ID_STORAGE_KEY,
    VCS_WAITING_LIST_ID_STORAGE_KEY,
    VCS_WORKSPACE_ID_STORAGE_KEY,
    WSSURL_STORAGE_KEY,
} from 'consts';
import { clearWaitingBannerDismissState } from 'features/AccessRequests/utils/waitingBannerDismiss';
import { clearWaitingListRegistry } from 'features/AccessRequests/utils/waitingListRegistry';
import { clearCallPolicySession } from '../policy';
import type { JoinCredentials } from './joinApi';
import { mustJoinThroughWaitingRoomApi } from './joinPlan';

/**
 * Сброс состояния звонка в sessionStorage при старте meet-окна (Electron).
 * Не sessionStorage.clear(): иначе гонка до CALL_DATA_CHANGE и потеря waitingListId.
 */
let isMeetCallSessionStorageReset = false;

const SESSION_KEYS_PRESERVE_ON_PAGE_RELOAD = [
    VCS_CALL_ID_STORAGE_KEY,
    VCS_WORKSPACE_ID_STORAGE_KEY,
    VCS_CALL_INITIATOR_ID_STORAGE_KEY,
    VCS_MEET_MESSENGER_BOOTSTRAP_ROOM_KEY,
    VCS_CALL_POLICY_SESSION_KEY,
    MEET_BOOTSTRAP_NAME_STORAGE_KEY,
] as const;

/** F5: sessionStorage переживает reload в той же вкладке — маркер читается в webEntry до clear. */
export const markMeetPageReloadSession = (): void => {
    sessionStorage.setItem(MEET_PAGE_RELOAD_SESSION_KEY, '1');
};

/**
 * webEntry: полный clear при первом заходе; при F5 сохраняем bootstrap (callId, policy), но не waiting room.
 */
export const resetSessionStorageForMeetBoot = (): void => {
    const isPageReload = sessionStorage.getItem(MEET_PAGE_RELOAD_SESSION_KEY) === '1';
    sessionStorage.removeItem(MEET_PAGE_RELOAD_SESSION_KEY);

    if (!isPageReload) {
        sessionStorage.clear();
        return;
    }

    const preserved: Record<string, string> = {};
    SESSION_KEYS_PRESERVE_ON_PAGE_RELOAD.forEach((key) => {
        const value = sessionStorage.getItem(key);
        if (value != null) {
            preserved[key] = value;
        }
    });

    sessionStorage.clear();

    Object.entries(preserved).forEach(([key, value]) => {
        sessionStorage.setItem(key, value);
    });
    sessionStorage.setItem(VCS_PAGE_RELOADING_KEY, '1');
};

export const resetMeetCallSessionStorage = (): void => {
    if (isMeetCallSessionStorageReset) {
        return;
    }
    isMeetCallSessionStorageReset = true;

    [
        IN_ROOM,
        TOKEN_STORAGE_KEY,
        WSSURL_STORAGE_KEY,
        VCS_WAITING_LIST_ID_STORAGE_KEY,
        VCS_WAITING_CALL_ID_STORAGE_KEY,
        VCS_CALL_ID_STORAGE_KEY,
        VCS_WORKSPACE_ID_STORAGE_KEY,
        VCS_CALL_INITIATOR_ID_STORAGE_KEY,
        MEET_BOOTSTRAP_NAME_STORAGE_KEY,
        EXTERNAL_NAME_STORAGE_KEY,
        VCS_PAGE_RELOADING_KEY,
        VCS_DISMISSED_WAITING_IDS_KEY,
        VCS_DISMISSED_WAITING_SET_KEY,
    ].forEach((key) => sessionStorage.removeItem(key));

    for (let i = sessionStorage.length - 1; i >= 0; i -= 1) {
        const key = sessionStorage.key(i);
        if (key?.startsWith(VCS_MODERATOR_BYPASS_PREFIX)) {
            sessionStorage.removeItem(key);
        }
    }

    clearCallPolicySession();
    clearWaitingListRegistry();
    clearWaitingBannerDismissState();
};

export type CallSessionPatch = {
    initiatorId?: string | number;
    callId?: string | number;
    workspaceId?: string | number;
    waitingListId?: string;
    livekitAuth?: { jwtToken: string; url: string };
    roomName?: string;
    token?: string | null;
    wssUrl?: string | null;
};

/** Pub-sub для waitingListId — единственный канал реактивности React-слою. */
const waitingListIdListeners = new Set<() => void>();
const notifyWaitingListIdChange = (): void => {
    waitingListIdListeners.forEach((fn) => fn());
};
export const subscribeWaitingListIdChange = (listener: () => void): (() => void) => {
    waitingListIdListeners.add(listener);
    return () => {
        waitingListIdListeners.delete(listener);
    };
};

const persistCallIds = (patch: CallSessionPatch): void => {
    if (patch.initiatorId != null) {
        sessionStorage.setItem(VCS_CALL_INITIATOR_ID_STORAGE_KEY, String(patch.initiatorId));
    }
    if (patch.callId != null && Number(patch.callId) > 0) {
        sessionStorage.setItem(VCS_CALL_ID_STORAGE_KEY, String(patch.callId));
    }
    if (patch.workspaceId != null && Number(patch.workspaceId) > 0) {
        sessionStorage.setItem(VCS_WORKSPACE_ID_STORAGE_KEY, String(patch.workspaceId));
    }
};

/**
 * Флаг активного join API-запроса. Пока он выставлен, applyCallSessionPatch пропускает
 * сброс queue/токенов при смене callId — ответ API ещё не вернулся и сброс сломает сессию.
 */
let isJoinApiInProgress = false;

export const lockJoinSession = (): void => {
    isJoinApiInProgress = true;
};

export const unlockJoinSession = (): void => {
    isJoinApiInProgress = false;
};

/** Единая точка обновления session: call ids, очередь ожидания, LiveKit JWT. */
export const applyCallSessionPatch = (patch: CallSessionPatch): void => {
    const previousCallId = sessionStorage.getItem(VCS_CALL_ID_STORAGE_KEY);
    const incomingCallId = patch.callId != null && Number(patch.callId) > 0 ? String(patch.callId) : undefined;

    if (!isJoinApiInProgress && previousCallId && incomingCallId && previousCallId !== incomingCallId) {
        clearWaitingQueue();
        clearLivekitTokens();
        sessionStorage.removeItem(IN_ROOM);
    }

    persistCallIds(patch);

    if (patch.waitingListId) {
        enterWaitingQueue(patch.waitingListId);
        return;
    }

    if (isHasWaitingListId()) {
        const currentCallId = sessionStorage.getItem(VCS_CALL_ID_STORAGE_KEY);
        if (currentCallId) {
            sessionStorage.setItem(VCS_WAITING_CALL_ID_STORAGE_KEY, currentCallId);
        }
        return;
    }

    if (patch.token && patch.wssUrl) {
        storeLivekitTokens({ jwtToken: patch.token, wssUrl: patch.wssUrl });
        clearWaitingQueue();
        return;
    }

    if (!isAdmittedVcsSession()) {
        sessionStorage.removeItem(IN_ROOM);
    }

    if (patch.livekitAuth && patch.roomName && !isAdmittedVcsSession()) {
        if (!mustJoinThroughWaitingRoomApi(patch.roomName)) {
            storeLivekitTokens({ jwtToken: patch.livekitAuth.jwtToken, wssUrl: patch.livekitAuth.url });
            clearWaitingQueue();
        } else {
            clearLivekitTokens();
        }
        return;
    }

    if (!isAdmittedVcsSession()) {
        clearLivekitTokens();
    }
};

export const isAdmittedVcsSession = (): boolean => {
    if (sessionStorage.getItem(IN_ROOM) === '1') return true;

    const hasTokens = Boolean(sessionStorage.getItem(TOKEN_STORAGE_KEY) && sessionStorage.getItem(WSSURL_STORAGE_KEY));

    // Используем scoped reader: устаревший waitingListId (от предыдущего callId) не блокирует admit.
    return hasTokens && !isHasWaitingListId();
};

/**
 * waitingListId, если он принадлежит ТЕКУЩЕМУ звонку (callId совпадает с тем, что был при enterWaitingQueue).
 * Если scope не совпадает — заявка протухла, возвращаем undefined. Фактическая чистка происходит
 * в applyCallSessionPatch при детекции смены callId; здесь — чистый read.
 */
/** Явная привязка заявки к звонку — единица scope-проверки. */
type WaitingEntry = { waitingListId: string; callId: string };

const readWaitingEntry = (): WaitingEntry | null => {
    const waitingListId = sessionStorage.getItem(VCS_WAITING_LIST_ID_STORAGE_KEY);
    if (!waitingListId) return null;
    const callId = sessionStorage.getItem(VCS_WAITING_CALL_ID_STORAGE_KEY) ?? '';
    return { waitingListId, callId };
};

const isWaitingListIdInScope = (): boolean => {
    const entry = readWaitingEntry();
    if (!entry) return true;

    const currentCallId = sessionStorage.getItem(VCS_CALL_ID_STORAGE_KEY);

    if (!entry.callId || !currentCallId) return true;

    return entry.callId === currentCallId;
};

export const getWaitingListId = (): string | undefined => {
    const entry = readWaitingEntry();
    return entry && isWaitingListIdInScope() ? entry.waitingListId : undefined;
};

export const isHasWaitingListId = (): boolean => Boolean(getWaitingListId());

export const isInRoom = (): boolean => sessionStorage.getItem(IN_ROOM) === '1';

export const clearLivekitTokens = (): void => {
    sessionStorage.removeItem(TOKEN_STORAGE_KEY);
    sessionStorage.removeItem(WSSURL_STORAGE_KEY);
};

export const storeLivekitTokens = (credentials: JoinCredentials): void => {
    sessionStorage.setItem(TOKEN_STORAGE_KEY, credentials.jwtToken);
    sessionStorage.setItem(WSSURL_STORAGE_KEY, credentials.wssUrl);
};

export const enterWaitingQueue = (waitingListId: string): void => {
    const previousInScope = getWaitingListId();
    if (previousInScope === waitingListId) {
        return;
    }
    sessionStorage.setItem(VCS_WAITING_LIST_ID_STORAGE_KEY, waitingListId);
    // Привязываем заявку к текущему callId, чтобы при смене звонка id протух.
    const currentCallId = sessionStorage.getItem(VCS_CALL_ID_STORAGE_KEY);
    if (currentCallId) {
        sessionStorage.setItem(VCS_WAITING_CALL_ID_STORAGE_KEY, currentCallId);
    } else {
        sessionStorage.removeItem(VCS_WAITING_CALL_ID_STORAGE_KEY);
    }
    clearLivekitTokens();
    sessionStorage.removeItem(IN_ROOM);
    notifyWaitingListIdChange();
};

export const clearWaitingQueue = (): void => {
    const previous = getWaitingListId();
    sessionStorage.removeItem(VCS_WAITING_LIST_ID_STORAGE_KEY);
    sessionStorage.removeItem(VCS_WAITING_CALL_ID_STORAGE_KEY);
    if (previous) {
        notifyWaitingListIdChange();
    }
};

export const markAdmittedToRoom = (): void => {
    const previous = getWaitingListId();
    sessionStorage.setItem(IN_ROOM, '1');
    sessionStorage.removeItem(VCS_WAITING_LIST_ID_STORAGE_KEY);
    sessionStorage.removeItem(VCS_WAITING_CALL_ID_STORAGE_KEY);
    if (previous) {
        notifyWaitingListIdChange();
    }
};

export const clearInRoomFlag = (): void => {
    sessionStorage.removeItem(IN_ROOM);
};

export const getCallIdAndWorkspaceId = (): { callId?: number; workspaceId?: number } => {
    const callIdRaw = sessionStorage.getItem(VCS_CALL_ID_STORAGE_KEY);
    const workspaceIdRaw = sessionStorage.getItem(VCS_WORKSPACE_ID_STORAGE_KEY);

    const callId = callIdRaw ? Number(callIdRaw) : undefined;
    const workspaceId = workspaceIdRaw ? Number(workspaceIdRaw) : undefined;

    return {
        callId: callId != null && Number.isFinite(callId) && callId > 0 ? callId : undefined,
        workspaceId: workspaceId != null && Number.isFinite(workspaceId) && workspaceId > 0 ? workspaceId : undefined,
    };
};
